/**
 * Chairman Dashboard
 * Dashboard for chairman to oversee operations
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import MainLayout from '../components/Layout/MainLayout';
import chairmanReportsService from '../services/chairmanReportsService';
import applicationService from '../services/applicationService';
import corporateService from '../services/corporateService';
import {
  MdTrendingUp,
  MdFlightTakeoff,
  MdAssessment,
  MdPublic,
  MdAssignment,
  MdBusiness,
  MdAdd,
  MdDelete,
  MdBadge
} from 'react-icons/md';

const ChairmanDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [corporates, setCorporates] = useState([]);
  const [newCorporateName, setNewCorporateName] = useState('');
  const [showAddCorporateModal, setShowAddCorporateModal] = useState(false);
  const [stats, setStats] = useState({
    totalApplications: 0,
    completedApplications: 0,
    successRate: 0,
    totalCountries: 0,
    departedCount: 0,
    interviewCount: 0,
    examCount: 0
  });

  useEffect(() => {
    fetchDashboardStats();
    fetchCorporates();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);

      // Fetch multiple reports in parallel
      const [summaryResponse, filterOptions] = await Promise.all([
        chairmanReportsService.getSummaryReport({}).catch(() => ({ success: false })),
        applicationService.getFilterOptions().catch(() => ({ success: false }))
      ]);

      const newStats = {
        totalApplications: 0,
        completedApplications: 0,
        successRate: 0,
        totalCountries: 0,
        departedCount: 0,
        interviewCount: 0,
        examCount: 0
      };

      // Process summary report - it now contains direct application counts
      if (summaryResponse?.success && summaryResponse.data) {
        const data = summaryResponse.data;
        const summary = data.summary;

        // Get direct application counts from the new structure
        newStats.totalApplications = data.totalApplications || 0;
        newStats.completedApplications = data.completedApplications || 0;

        // Get departure data
        if (summary?.departure) {
          newStats.departedCount = summary.departure.departedCount || 0;
        }

        // Get interview data for additional stats
        if (summary?.interview) {
          const interviewTotal =
            (summary.interview.acceptedCount || 0) +
            (summary.interview.rejectedCount || 0) +
            (summary.interview.redirectedCount || 0);
          newStats.interviewCount = interviewTotal;
        }

        // Calculate success rate
        if (newStats.totalApplications > 0) {
          newStats.successRate = Math.round((newStats.completedApplications / newStats.totalApplications) * 100);
        }
      }

      // Process filter options for countries
      if (filterOptions?.success && filterOptions.data) {
        newStats.totalCountries = filterOptions.data.countries?.length || 0;
      }

      setStats(newStats);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCorporates = async () => {
    try {
      const response = await corporateService.getAllCorporates();
      if (response.success) {
        setCorporates(response.data);
      }
    } catch (error) {
      console.error('Error fetching corporates:', error);
    }
  };

  const handleAddCorporate = async () => {
    if (!newCorporateName.trim()) {
      toast.error('Please enter a corporate name');
      return;
    }

    try {
      const response = await corporateService.createCorporate({ name: newCorporateName.trim() });
      if (response.success) {
        toast.success('Corporate added successfully');
        setNewCorporateName('');
        setShowAddCorporateModal(false);
        fetchCorporates();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add corporate');
    }
  };

  const handleDeleteCorporate = async (id) => {
    if (!window.confirm('Are you sure you want to delete this corporate?')) {
      return;
    }

    try {
      const response = await corporateService.deleteCorporate(id);
      if (response.success) {
        toast.success('Corporate deleted successfully');
        fetchCorporates();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete corporate');
    }
  };

  const rightSidebarContent = (
    <div className="p-4">
      <h3 className="text-base font-semibold text-charcoal mb-4 pb-2 border-b border-border">Quick Stats</h3>
      <div className="flex flex-col gap-3">
        <div className="flex justify-between items-center p-3 bg-background-secondary rounded-lg">
          <span className="text-xs text-text-secondary font-medium">Total Applications</span>
          <span className="text-sm font-semibold text-charcoal">{loading ? '...' : stats.totalApplications}</span>
        </div>
        <div className="flex justify-between items-center p-3 bg-background-secondary rounded-lg">
          <span className="text-xs text-text-secondary font-medium">Completed</span>
          <span className="text-sm font-semibold text-olive">{loading ? '...' : stats.completedApplications}</span>
        </div>
        <div className="flex justify-between items-center p-3 bg-background-secondary rounded-lg">
          <span className="text-xs text-text-secondary font-medium">Success Rate</span>
          <span className="text-sm font-semibold text-olive">{loading ? '...' : `${stats.successRate}%`}</span>
        </div>
        <div className="flex justify-between items-center p-3 bg-background-secondary rounded-lg">
          <span className="text-xs text-text-secondary font-medium">Departed</span>
          <span className="text-sm font-semibold text-accent-green">{loading ? '...' : stats.departedCount}</span>
        </div>
        <div className="flex justify-between items-center p-3 bg-background-secondary rounded-lg">
          <span className="text-xs text-text-secondary font-medium">Countries</span>
          <span className="text-sm font-semibold text-charcoal">{loading ? '...' : stats.totalCountries}</span>
        </div>
      </div>
    </div>
  );

  return (
    <MainLayout rightSidebarContent={rightSidebarContent}>
      <div className="min-h-screen bg-background p-lg">
        <div className="flex justify-between items-center mb-xl p-lg px-xl bg-surface rounded-lg shadow-sm border border-border-light">
          <div>
            <h1 className="text-[28px] text-primary m-0 font-semibold tracking-[-0.5px]">Chairman Dashboard</h1>
            <p className="text-sm text-text-secondary mt-1 font-normal">Executive overview and strategic decisions</p>
          </div>
          <button
            onClick={fetchDashboardStats}
            disabled={loading}
            className="px-4 py-2 bg-accent-green text-white rounded-lg hover:bg-accent-green-dark transition-colors disabled:opacity-50"
          >
            {loading ? 'Refreshing...' : 'Refresh Stats'}
          </button>
        </div>

        <div className="grid grid-cols-1 gap-lg max-w-full">
          <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-lg col-span-full">
            <div className="bg-surface p-lg rounded-lg shadow-sm border border-border-light flex flex-col items-center text-center gap-md transition-all duration-200 hover:shadow-md hover:-translate-y-0.5">
              <MdTrendingUp className="text-[32px] w-14 h-14 flex items-center justify-center bg-background-light rounded-md flex-shrink-0 text-accent-green" size={32} />
              <div className="flex-1 flex flex-col items-center">
                <h3 className="text-[13px] text-text-secondary m-0 mb-1 font-medium uppercase tracking-wider">Total Applications</h3>
                <p className="text-2xl text-primary m-0 font-bold leading-none">
                  {loading ? '...' : stats.totalApplications}
                </p>
              </div>
            </div>

            <div className="bg-surface p-lg rounded-lg shadow-sm border border-border-light flex flex-col items-center text-center gap-md transition-all duration-200 hover:shadow-md hover:-translate-y-0.5">
              <MdFlightTakeoff className="text-[32px] w-14 h-14 flex items-center justify-center bg-background-light rounded-md flex-shrink-0 text-accent-green" size={32} />
              <div className="flex-1 flex flex-col items-center">
                <h3 className="text-[13px] text-text-secondary m-0 mb-1 font-medium uppercase tracking-wider">Successful Departures</h3>
                <p className="text-2xl text-primary m-0 font-bold leading-none">
                  {loading ? '...' : stats.departedCount}
                </p>
              </div>
            </div>

            <div className="bg-surface p-lg rounded-lg shadow-sm border border-border-light flex flex-col items-center text-center gap-md transition-all duration-200 hover:shadow-md hover:-translate-y-0.5">
              <MdAssessment className="text-[32px] w-14 h-14 flex items-center justify-center bg-background-light rounded-md flex-shrink-0 text-accent-green" size={32} />
              <div className="flex-1 flex flex-col items-center">
                <h3 className="text-[13px] text-text-secondary m-0 mb-1 font-medium uppercase tracking-wider">Success Rate</h3>
                <p className="text-2xl text-primary m-0 font-bold leading-none">
                  {loading ? '...' : `${stats.successRate}%`}
                </p>
              </div>
            </div>

            <div className="bg-surface p-lg rounded-lg shadow-sm border border-border-light flex flex-col items-center text-center gap-md transition-all duration-200 hover:shadow-md hover:-translate-y-0.5">
              <MdPublic className="text-[32px] w-14 h-14 flex items-center justify-center bg-background-light rounded-md flex-shrink-0 text-accent-green" size={32} />
              <div className="flex-1 flex flex-col items-center">
                <h3 className="text-[13px] text-text-secondary m-0 mb-1 font-medium uppercase tracking-wider">Countries</h3>
                <p className="text-2xl text-primary m-0 font-bold leading-none">
                  {loading ? '...' : stats.totalCountries}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-surface p-xl rounded-lg shadow-sm border border-border-light transition-all duration-200 hover:shadow-md">
            <h3 className="text-lg text-primary mb-lg pb-md border-b-2 border-border-light font-semibold">Reports & Analytics</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-md">
              <button
                onClick={() => navigate('/chairman/reports/departure')}
                className="flex items-center gap-md p-md bg-background-light rounded-lg border border-border hover:bg-accent-green hover:text-white transition-all duration-200 group"
              >
                <MdFlightTakeoff className="text-xl text-accent-green group-hover:text-white" />
                <div className="text-left">
                  <div className="text-sm font-semibold text-primary group-hover:text-white">Departure Report</div>
                  <div className="text-xs text-text-secondary group-hover:text-white">View departure statistics</div>
                </div>
              </button>

              <button
                onClick={() => navigate('/chairman/daily-reports')}
                className="flex items-center gap-md p-md bg-background-light rounded-lg border border-border hover:bg-accent-green hover:text-white transition-all duration-200 group"
              >
                <MdAssignment className="text-xl text-accent-green group-hover:text-white" />
                <div className="text-left">
                  <div className="text-sm font-semibold text-primary group-hover:text-white">Daily Reports</div>
                  <div className="text-xs text-text-secondary group-hover:text-white">HR end-of-day submissions</div>
                </div>
              </button>

              <button
                onClick={() => setShowAddCorporateModal(true)}
                className="flex items-center gap-md p-md bg-background-light rounded-lg border border-border hover:bg-accent-green hover:text-white transition-all duration-200 group"
              >
                <MdBusiness className="text-xl text-accent-green group-hover:text-white" />
                <div className="text-left">
                  <div className="text-sm font-semibold text-primary group-hover:text-white">Manage Corporates</div>
                  <div className="text-xs text-text-secondary group-hover:text-white">Add or remove corporate names</div>
                </div>
              </button>

              <button
                onClick={() => navigate('/admin/id-card')}
                className="flex items-center gap-md p-md bg-background-light rounded-lg border border-border hover:bg-accent-green hover:text-white transition-all duration-200 group"
              >
                <MdBadge className="text-xl text-accent-green group-hover:text-white" />
                <div className="text-left">
                  <div className="text-sm font-semibold text-primary group-hover:text-white">ID Card Generator</div>
                  <div className="text-xs text-text-secondary group-hover:text-white">Generate HR ID cards</div>
                </div>
              </button>
            </div>
          </div>

          <div className="bg-surface p-xl rounded-lg shadow-sm border border-border-light transition-all duration-200 hover:shadow-md">
            <div className="flex justify-between items-center mb-lg pb-md border-b-2 border-border-light">
              <h3 className="text-lg text-primary font-semibold">Corporate Management</h3>
              <button
                onClick={() => setShowAddCorporateModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-accent-green text-white rounded-lg hover:bg-accent-green-dark transition-colors"
              >
                <MdAdd size={20} />
                Add Corporate
              </button>
            </div>
            <div className="space-y-2">
              {corporates.length === 0 ? (
                <p className="text-text-secondary text-sm text-center py-4">No corporates added yet</p>
              ) : (
                corporates.map((corporate) => (
                  <div
                    key={corporate._id}
                    className="flex justify-between items-center p-3 bg-background-light rounded-lg border border-border hover:border-accent-green transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <MdBusiness className="text-accent-green" size={20} />
                      <span className="text-sm font-medium text-primary">{corporate.name}</span>
                    </div>
                    <button
                      onClick={() => handleDeleteCorporate(corporate._id)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete corporate"
                    >
                      <MdDelete size={18} />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="bg-surface p-xl rounded-lg shadow-sm border border-border-light transition-all duration-200 hover:shadow-md">
            <h3 className="text-lg text-primary mb-lg pb-md border-b-2 border-border-light font-semibold">Your Profile</h3>
            <div className="flex justify-between items-center py-md border-b border-border-light">
              <span className="text-text-secondary font-medium text-sm">Phone Number:</span>
              <span className="text-text-primary font-semibold text-sm">{user?.phoneNumber}</span>
            </div>
            {user?.email && (
              <div className="flex justify-between items-center py-md border-b border-border-light">
                <span className="text-text-secondary font-medium text-sm">Email:</span>
                <span className="text-text-primary font-semibold text-sm">{user.email}</span>
              </div>
            )}
            <div className="flex justify-between items-center py-md">
              <span className="text-text-secondary font-medium text-sm">Role:</span>
              <span className="px-3.5 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider bg-status-warning-bg text-[#B8842E]">CHAIRMAN</span>
            </div>
          </div>
        </div>
      </div>

      {/* Add Corporate Modal */}
      {showAddCorporateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-primary mb-4">Add New Corporate</h3>
            <input
              type="text"
              value={newCorporateName}
              onChange={(e) => setNewCorporateName(e.target.value)}
              placeholder="Enter corporate name"
              className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-green mb-4"
              onKeyPress={(e) => e.key === 'Enter' && handleAddCorporate()}
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowAddCorporateModal(false);
                  setNewCorporateName('');
                }}
                className="px-4 py-2 border border-border rounded-lg hover:bg-background-light transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddCorporate}
                className="px-4 py-2 bg-accent-green text-white rounded-lg hover:bg-accent-green-dark transition-colors"
              >
                Add Corporate
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default ChairmanDashboard;
